import { getCurrentIdToken } from '../auth/AuthContext';

/**
 * タグの型定義
 */
export interface Tag {
  id: string;
  name: string;
  nameEn?: string;
  slug: string;
  isVisible: boolean;
  usageCount: number;
}

/**
 * API GatewayのベースエンドポイントURLを取得
 * 環境変数 VITE_LAMBDA_ENDPOINT から取得し、環境に応じてステージを追加
 */
const getApiEndpoint = (): string => {
  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT;
  if (endpoint) {
    let cleanEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    
    const isCloudFront = cleanEndpoint.includes('cloudfront.net');
    
    if (isCloudFront) {
      return cleanEndpoint;
    }
    
    const urlMatch = cleanEndpoint.match(/amazonaws\.com(\/.*)?$/);
    const pathAfterAws = urlMatch ? (urlMatch[1] || '') : '';
    const hasStage = pathAfterAws.length > 0 && pathAfterAws !== '/';
    
    if (!hasStage) {
      if (import.meta.env.DEV) {
        cleanEndpoint = `${cleanEndpoint}/dev`;
      } else if (import.meta.env.PROD) {
        cleanEndpoint = `${cleanEndpoint}/prod`;
      }
    }
    
    return cleanEndpoint;
  }
  
  const baseUrl = 'https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com';
  if (import.meta.env.DEV) {
    return `${baseUrl}/dev`;
  } else if (import.meta.env.PROD) {
    return `${baseUrl}/prod`;
  }
  return `${baseUrl}/dev`;
};

/**
 * タグ一覧を取得する（認証不要、公開エンドポイント）
 */
export interface GetTagsResponse {
  tags: Tag[];
}

/**
 * 公開用のタグ一覧を取得する（認証不要）
 */
export const getPublicTags = async (): Promise<GetTagsResponse> => {
  const endpoint = getApiEndpoint();
  const url = `${endpoint}/tags`;
  
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.tags || !Array.isArray(data.tags)) {
      throw new Error('レスポンス形式が不正です');
    }
    
    return data as GetTagsResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('CORSエラー: /tagsエンドポイントのCORS設定を確認してください。');
    }
    throw error;
  }
};

/**
 * 管理用のタグ一覧を取得する（認証必要、管理エンドポイント）
 */
export const getTags = async (): Promise<GetTagsResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/tags`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetTagsResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    if (!data.tags || !Array.isArray(data.tags)) {
      throw new Error('レスポンス形式が不正です');
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * 個別タグを取得する
 */
export interface GetTagResponse {
  tag: Tag;
}

export const getTag = async (tagId: string): Promise<GetTagResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/tags/${encodeURIComponent(tagId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'タグが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: GetTagResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    if (!data.tag) {
      throw new Error('レスポンス形式が不正です');
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。');
    }
    
    throw error;
  }
};

/**
 * タグを作成する
 */
export interface CreateTagRequest {
  name: string;
  nameEn?: string;
  slug?: string;
  isVisible?: boolean;
  usageCount?: number;
}

export interface CreateTagResponse {
  tag: Tag;
}

export const createTag = async (request: CreateTagRequest): Promise<CreateTagResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/tags`;
  
  // バリデーション
  if (!request.name || !request.name.trim()) {
    throw new Error('nameパラメータが必須です');
  }
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const requestBody: any = {
      name: request.name.trim(),
    };
    
    if (request.nameEn) {
      requestBody.name_en = request.nameEn.trim();
    }
    
    if (request.slug) {
      requestBody.slug = request.slug.trim();
    }
    
    if (request.isVisible !== undefined) {
      requestBody.is_visible = request.isVisible;
    }
    
    if (request.usageCount !== undefined) {
      requestBody.usage_count = request.usageCount;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: CreateTagResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    if (!data.tag) {
      throw new Error('レスポンス形式が不正です');
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * タグを更新する
 */
export interface UpdateTagRequest {
  name?: string;
  nameEn?: string;
  slug?: string;
  isVisible?: boolean;
  usageCount?: number;
}

export interface UpdateTagResponse {
  tag: Tag;
}

export const updateTag = async (tagId: string, request: UpdateTagRequest): Promise<UpdateTagResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/tags/${encodeURIComponent(tagId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const requestBody: any = {};
    
    if (request.name !== undefined) {
      requestBody.name = request.name.trim();
    }
    
    if (request.nameEn !== undefined) {
      requestBody.name_en = request.nameEn.trim();
    }
    
    if (request.slug !== undefined) {
      requestBody.slug = request.slug.trim();
    }
    
    if (request.isVisible !== undefined) {
      requestBody.is_visible = request.isVisible;
    }
    
    if (request.usageCount !== undefined) {
      requestBody.usage_count = request.usageCount;
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'タグが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: UpdateTagResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    if (!data.tag) {
      throw new Error('レスポンス形式が不正です');
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * タグを削除する
 */
export interface DeleteTagResponse {
  message: string;
}

export const deleteTag = async (tagId: string): Promise<DeleteTagResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  const url = `${endpoint}/admin/tags/${encodeURIComponent(tagId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {};
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = 'タグが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    let data: DeleteTagResponse;
    if (responseData.body) {
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      data = responseData;
    }
    
    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * タグ名生成リクエスト
 */
export interface GenerateTagNameEnAndSlugRequest {
  tagName: string;
  prompt: string;
}

/**
 * タグ名生成レスポンス
 */
export interface GenerateTagNameEnAndSlugResponse {
  nameEn: string;
  slug: string;
}

/**
 * タグ名生成用JSONスキーマ
 */
const generateTagNameEnAndSlugJsonSchema = {
  type: 'object',
  properties: {
    nameEn: {
      type: 'string',
      description: 'タグ名（英語）',
    },
    slug: {
      type: 'string',
      description: 'URLフレンドリーなSlug（小文字の英数字とハイフンのみ）',
    },
  },
  required: ['nameEn', 'slug'],
};

/**
 * 日本語タグ名から英語名とSlugを自動生成する
 * AWS Bedrock（Claude Haiku 4.5、inference profile経由）を使用
 * 構造化出力（structured output）を使用して確実に値を取得
 * 
 * @param request 生成リクエスト（日本語タグ名とプロンプト）
 * @returns 生成された英語名とSlug
 */
export const generateTagNameEnAndSlug = async (
  request: GenerateTagNameEnAndSlugRequest
): Promise<GenerateTagNameEnAndSlugResponse> => {
  const { tagName, prompt } = request;
  
  // バリデーション
  if (!tagName || typeof tagName !== 'string' || tagName.trim() === '') {
    throw new Error('tagNameパラメータが必須です');
  }
  if (!prompt || typeof prompt !== 'string' || prompt.trim() === '') {
    throw new Error('promptパラメータが必須です');
  }
  
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント（generatePostContentと同じエンドポイントを使用）
  const url = `${endpoint}/admin/generate-post-content`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const requestBody: any = {
      prompt: prompt.trim(),
      jsonSchema: generateTagNameEnAndSlugJsonSchema,
      toolName: 'tag_name_generator',
      toolDescription: '日本語タグ名から英語名とSlugを生成します。',
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストエラー';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 401) {
        // 401 Unauthorizedエラーの場合
        let errorMessage = '認証が必要です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 403) {
        // 認証エラーの場合
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 500) {
        let errorMessage = 'サーバーエラーが発生しました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 504) {
        // 504 Gateway Timeoutエラーの場合
        let errorMessage = 'リクエストがタイムアウトしました。AI生成に時間がかかっている可能性があります。しばらく待ってから再度お試しください。';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // レスポンスデータにnameEnとslugが含まれていることを確認
    if (!data.nameEn || !data.slug) {
      throw new Error('生成された英語名またはSlugが不足しています');
    }
    return data as GenerateTagNameEnAndSlugResponse;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: /admin/generate-post-contentエンドポイントのCORS設定を確認してください。' +
        'API GatewayでOPTIONSメソッドが有効化され、適切なCORSヘッダーが設定されている必要があります。' +
        '開発環境（localhost:5173）からのリクエストが許可されているか確認してください。'
      );
    }
    
    throw error;
  }
};

